// src/components/Propuestas.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye,
  FiSend, FiCheckCircle, FiClock, FiX,
  FiUser, FiCalendar, FiDownload,
  FiFileText, FiRefreshCw,
  FiAlertCircle
} from 'react-icons/fi';
import './Propuestas.css';
import PropuestaEditor from './PropuestaEditor';
import { useAuth } from '../context/AuthContext';
import { CASE_TYPES } from '../constants/caseTypes';
import { contractFee } from '../utils/contractFees';
import { calculatePaymentOptions, clampPaymentOptionCount, formatPaymentMoney } from '../utils/paymentOptions';
import { formatDateTimeUS, formatDateUS } from '../utils/dateFormat';
import { downloadProtectedDocument, useProtectedDocumentUrl } from '../utils/protectedDocuments';
import { isPersonalInjuryRole } from '../accessPolicy';

const DOCUMENT_TYPES = {
  propuesta: { label: '📄 Propuesta', plain: 'Propuesta', icon: '📄' },
  contrato: { label: '📝 Contrato', plain: 'Contrato', icon: '📝' },
  plan_pagos: { label: '💳 Plan de pagos', plain: 'Plan de pagos', icon: '💳' }
};
const documentType = (type) => DOCUMENT_TYPES[type] || DOCUMENT_TYPES.propuesta;
const PROCESS_SEPARATOR = ', ';
const composeProcessTypes = (types = [], custom = '') => {
  // Continue accepting the former pipe separator when editing historic records.
  const values = [...types, ...String(custom).split(/[|,]/).map((item) => item.trim())]
    .filter(Boolean);
  return [...new Set(values)].join(PROCESS_SEPARATOR);
};

const Propuestas = () => {
  const { usuario } = useAuth();
  const [propuestas, setPropuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [showVerModal, setShowVerModal] = useState(false);
  const [showEnviarModal, setShowEnviarModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedPropuesta, setSelectedPropuesta] = useState(null);
  const [editingPropuesta, setEditingPropuesta] = useState(null);
  const [editingPackageDocuments, setEditingPackageDocuments] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loadingEnvio, setLoadingEnvio] = useState(false);

  const [nuevaPropuesta, setNuevaPropuesta] = useState({
    cliente_id: '',
    titulo: '',
    tipo: 'propuesta',
    contenido: '',
    abogado_responsable: 'Martin Quiroz, Esq.',
    notas: '',
    lead_id: null, nombre_destinatario: '', honorarios: 0, tipo_caso: '', tipos_caso: [], tipo_caso_personalizado: '', idioma: 'es', tipo_tarifa_gubernamental: 'Fee USCIS', tarifa_gubernamental: 0, opciones_pago: 2,
    destinatario_tipo: 'lead', deposito_inicial: 0, numero_cuotas: 1, frecuencia: 'mensual', fecha_primer_pago: '', metodo_pago: 'card_one'
  });

  const [envioData, setEnvioData] = useState({
    email_cliente: '',
    mensaje_adicional: ''
  });

  const toggleCaseType = (value) => setNuevaPropuesta((current) => {
    const types = current.tipos_caso.includes(value)
      ? current.tipos_caso.filter((item) => item !== value)
      : [...current.tipos_caso, value];
    return { ...current, tipos_caso: types,
      tipo_tarifa_gubernamental: contractFee(types.join(', '), current.tipo_tarifa_gubernamental) };
  });

  const API_URL = window.__CRM_API__ || 'http://localhost:8000/api';
  const canViewGeneratedPdfs = !isPersonalInjuryRole(usuario?.rol);
  const previewEndpoint = showVerModal && selectedPropuesta && canViewGeneratedPdfs
    ? `${API_URL}/propuestas/${selectedPropuesta.id}/preview`
    : '';
  const proposalPreview = useProtectedDocumentUrl(previewEndpoint, Boolean(previewEndpoint));
  const authConfig = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Plantillas en texto plano
  const plantillasPredefinidas = useMemo(() => ({
    propuesta: `📄 PROPUESTA DE SERVICIOS LEGALES

Fecha: {fecha}
Cliente: {nombre_cliente}

OBJETO DE LA PROPUESTA
La presente propuesta tiene como objeto ofrecer los servicios legales de Abogados Quiroz para el caso de {nombre_cliente}.

ALCANCE DE LOS SERVICIOS
Se incluyen los siguientes servicios:
• Análisis legal del caso
• Preparación y presentación de documentos
• Representación legal
• Seguimiento del caso

HONORARIOS
Los honorarios profesionales serán de $5,000.00.

VIGENCIA
Esta propuesta tiene una vigencia de 8 días a partir de la fecha de envío.

Abogados Quiroz - Comprometidos con tu caso`,

    contrato: `📝 CONTRATO DE SERVICIOS LEGALES

Fecha: {fecha}
Cliente: {nombre_cliente}

CLAUSULA PRIMERA - OBJETO
El presente contrato tiene por objeto la prestación de servicios legales por parte de Abogados Quiroz a favor de {nombre_cliente}.

CLAUSULA SEGUNDA - OBLIGACIONES
El abogado se obliga a:
• Brindar asesoría legal integral
• Representar al cliente en todas las instancias
• Mantener confidencialidad de la información

CLAUSULA TERCERA - HONORARIOS
El cliente pagará la suma de $5,000.00 por los servicios profesionales.

CLAUSULA CUARTA - VIGENCIA
El presente contrato tendrá una vigencia de 8 días a partir de la fecha de envío.

Abogados Quiroz - Comprometidos con tu caso`
  }), []);

  // La recarga se ejecuta al abrir el Workspace; las mutaciones llaman cargarDatos explícitamente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cargarDatos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nuevaPropuesta.tipo && !nuevaPropuesta.contenido) {
      const plantilla = plantillasPredefinidas[nuevaPropuesta.tipo] || '';
      setNuevaPropuesta(prev => ({
        ...prev,
        contenido: plantilla
      }));
    }
  }, [nuevaPropuesta.tipo, nuevaPropuesta.contenido, plantillasPredefinidas]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const config = authConfig();

      const [propuestasRes, clientesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/propuestas/`, config),
        axios.get(`${API_URL}/clientes/`, config),
        axios.get(`${API_URL}/leads/`, config)
      ]);

      setPropuestas(propuestasRes.data);
      setClientes(clientesRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============ FILTRADO ============
  const filteredPropuestas = propuestas.filter(p => !(p.package_id && p.tipo === 'plan_pagos')).filter(p => {
    const matchesSearch = p.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cliente_id?.toString().includes(searchTerm);
    const matchesEstado = filterEstado === 'todos' || p.estado === filterEstado;
    const matchesTipo = filterTipo === 'todos' || p.tipo === filterTipo;
    return matchesSearch && matchesEstado && matchesTipo;
  });

  // ============ PAGINACIÓN ============
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPropuestas = filteredPropuestas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPropuestas.length / itemsPerPage);

  // ============ FUNCIONES AUXILIARES ============
  const getEstadoBadge = (estado) => {
    const config = {
      borrador: { label: 'Borrador', color: '#95a5a6', icon: FiEdit2 },
      enviada: { label: 'Enviada', color: '#3498db', icon: FiSend },
      pendiente_firma: { label: 'Pendiente de firma', color: '#d97706', icon: FiClock },
      firmado: { label: 'Firmado', color: '#27ae60', icon: FiCheckCircle },
      vencido: { label: 'Vencido', color: '#e74c3c', icon: FiAlertCircle },
      rechazado: { label: 'Rechazado', color: '#e74c3c', icon: FiX }
    };
    return config[estado] || config.borrador;
  };

  const getEstadoIcon = (estado) => {
    const Icon = getEstadoBadge(estado).icon;
    return <Icon size={14} />;
  };

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellido}` : 'Cliente no encontrado';
  };

  const getClienteEmail = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente?.email || '';
  };

  const getLeadNombre = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? `${lead.nombre} ${lead.apellido}` : 'Lead no encontrado';
  };

  const getDestinatarioNombre = (propuesta) => propuesta.lead_id ? getLeadNombre(propuesta.lead_id) : getClienteNombre(propuesta.cliente_id);
  const getDestinatarioTipo = (propuesta) => propuesta.lead_id ? 'Lead' : 'Cliente';

  const formatFecha = (fecha) => formatDateUS(fecha, 'No enviada');

  const getVigenciaEstado = (propuesta) => {
    if (!propuesta.fecha_vencimiento) return null;
    const ahora = new Date();
    const vencimiento = new Date(propuesta.fecha_vencimiento);
    const diasRestantes = Math.ceil((vencimiento - ahora) / (1000 * 60 * 60 * 24));
    
    if (diasRestantes < 0) return { label: 'Vencida', color: '#e74c3c' };
    if (diasRestantes <= 2) return { label: `Vence en ${diasRestantes} días`, color: '#f39c12' };
    if (diasRestantes <= 5) return { label: `Vence en ${diasRestantes} días`, color: '#3498db' };
    return { label: `Vence en ${diasRestantes} días`, color: '#27ae60' };
  };

  const handleContenidoChange = (texto) => {
    setNuevaPropuesta({...nuevaPropuesta, contenido: texto});
  };
  const formatFechaHora = (fecha) => formatDateTimeUS(fecha, '');
  const getDestinatarioEmail = (propuesta) => propuesta.lead_id ? leads.find(l => l.id === propuesta.lead_id)?.email || '' : getClienteEmail(propuesta.cliente_id);

  const cargarPlantillaOficial = async (tipo) => {
    try {
      const response = await axios.get(`${API_URL}/propuestas/plantillas/base/${tipo}`);
      setNuevaPropuesta((actual) => ({ ...actual, tipo, contenido: response.data.contenido_html }));
    } catch (error) {
      alert('No fue posible cargar la plantilla oficial.');
    }
  };

  const abrirCrearDocumento = async () => {
    setShowCrearModal(true);
    try {
      const response = await axios.get(`${API_URL}/propuestas/plantillas/base/propuesta`);
      setNuevaPropuesta(actual => ({ ...actual, tipo: 'propuesta', contenido: response.data.contenido_html }));
    } catch {
      alert('No fue posible cargar la plantilla oficial de propuesta.');
    }
  };

  // ============ FUNCIONES CRUD ============
  
  // CREAR Y ENVIAR AUTOMÁTICAMENTE
  const handleCrearPropuesta = async (e) => {
    e.preventDefault();
    
    if (!nuevaPropuesta.lead_id && !nuevaPropuesta.cliente_id) {
      alert('⚠️ Debes seleccionar un cliente');
      return;
    }
    if (!nuevaPropuesta.titulo.trim()) {
      alert('⚠️ El título es obligatorio');
      return;
    }

    try {
      const config = authConfig();
      
      const tipoCaso = composeProcessTypes(nuevaPropuesta.tipos_caso, nuevaPropuesta.tipo_caso_personalizado);
      const propuestaData = {
        cliente_id: nuevaPropuesta.cliente_id ? parseInt(nuevaPropuesta.cliente_id) : null,
        titulo: nuevaPropuesta.titulo,
        tipo: nuevaPropuesta.tipo,
        contenido_html: nuevaPropuesta.contenido,
        abogado_responsable: nuevaPropuesta.abogado_responsable || '',
        notas: nuevaPropuesta.notas || '',
        lead_id: nuevaPropuesta.lead_id ? parseInt(nuevaPropuesta.lead_id) : null,
        nombre_destinatario: nuevaPropuesta.nombre_destinatario.trim() || null,
        honorarios: Number(nuevaPropuesta.honorarios || 0), tipo_caso: tipoCaso, idioma: nuevaPropuesta.idioma,
        tipo_tarifa_gubernamental: nuevaPropuesta.tipo_tarifa_gubernamental, tarifa_gubernamental: Number(nuevaPropuesta.tarifa_gubernamental || 0),
        opciones_pago: clampPaymentOptionCount(nuevaPropuesta.opciones_pago)
      };
      
      const response = nuevaPropuesta.tipo === 'contrato'
        ? await axios.post(`${API_URL}/propuestas/paquetes/contrato`, {
          lead_id: propuestaData.lead_id,
          cliente_id: propuestaData.cliente_id,
          titulo: propuestaData.titulo,
          tipo_caso: propuestaData.tipo_caso,
          honorarios: propuestaData.honorarios,
          tipo_tarifa_gubernamental: propuestaData.tipo_tarifa_gubernamental,
          tarifa_gubernamental: propuestaData.tarifa_gubernamental,
          deposito_inicial: Number(nuevaPropuesta.deposito_inicial || 0),
          numero_cuotas: Number(nuevaPropuesta.numero_cuotas || 1),
          frecuencia: nuevaPropuesta.frecuencia,
          fecha_primer_pago: nuevaPropuesta.fecha_primer_pago,
          metodo_pago: nuevaPropuesta.metodo_pago,
          idioma: propuestaData.idioma,
          abogado_responsable: propuestaData.abogado_responsable,
          notas: propuestaData.notas
        }, config)
        : await axios.post(`${API_URL}/propuestas/`, propuestaData, config);
      const nuevaPropuestaCreada = nuevaPropuesta.tipo === 'contrato' ? response.data.contract : response.data;
      const newPackageDocuments = nuevaPropuesta.tipo === 'contrato'
        ? [response.data.contract, response.data.payment_plan_document].filter(Boolean)
        : [nuevaPropuestaCreada];
      
      // Enviar automáticamente
      /* Envío manual: el usuario debe revisar el PDF oficial antes de enviar. */
      const emailCliente = '';
      if (emailCliente) {
        try {
          await axios.post(
            `${API_URL}/propuestas/${nuevaPropuestaCreada.id}/enviar`,
            {
              email_cliente: emailCliente,
              mensaje_adicional: `Estimado/a cliente, adjunto ${nuevaPropuesta.tipo} para su revisión.`
            },
            config
          );
        } catch (error) {
          console.warn('Error al enviar email:', error);
        }
      }
      
      setPropuestas(current => [...newPackageDocuments, ...current.filter(item => !newPackageDocuments.some(created => created.id === item.id))]);
      handleOpenEditor(nuevaPropuestaCreada, newPackageDocuments);
      
      setShowCrearModal(false);
      setNuevaPropuesta({
        cliente_id: '',
        titulo: '',
        tipo: 'propuesta',
        contenido: '',
        abogado_responsable: 'Martin Quiroz, Esq.',
        notas: '',
        lead_id: null, nombre_destinatario: '', honorarios: 0, tipo_caso: '', tipos_caso: [], tipo_caso_personalizado: '', idioma: 'es', tipo_tarifa_gubernamental: 'Fee USCIS', tarifa_gubernamental: 0, opciones_pago: 2,
        destinatario_tipo: 'lead', deposito_inicial: 0, numero_cuotas: 1, frecuencia: 'mensual', fecha_primer_pago: '', metodo_pago: 'card_one'
      });
      
      alert(`✅ ${documentType(nuevaPropuesta.tipo).plain} creado. Revísalo y usa Enviar cuando esté listo.`);
    } catch (error) {
      console.error('Error al crear propuesta:', error);
      alert(`❌ Error al crear o enviar: ${error.response?.data?.detail || error.message}`);
    }
  };

  // ENVIAR PROPUESTA
  const handleEnviarPropuesta = async () => {
    setLoadingEnvio(true);
    try {
      const config = authConfig();
      
      await axios.post(
        selectedPropuesta.package_id ? `${API_URL}/propuestas/paquetes/${selectedPropuesta.package_id}/enviar` : `${API_URL}/propuestas/${selectedPropuesta.id}/enviar`,
        envioData,
        config
      );
      
      const updated = propuestas.map(p => 
        p.id === selectedPropuesta.id ? { ...p, estado: 'enviada', fecha_envio: new Date().toISOString() } : p
      );
      setPropuestas(updated);
      
      setShowEnviarModal(false);
      setShowVerModal(false);
      alert('✅ Propuesta enviada exitosamente');
    } catch (error) {
      console.error('Error al enviar propuesta:', error);
      alert(`❌ Error al enviar: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingEnvio(false);
    }
  };

  // FIRMAR PROPUESTA
  const handleFirmarPropuesta = async (id) => {
    if (!window.confirm('Confirme únicamente después de verificar la firma electrónica o la evidencia firmada del cliente.')) return;
    try {
      const config = authConfig();
      
      const { data } = await axios.post(`${API_URL}/propuestas/${id}/firmar`, {}, config);
      
      // Reload the server's canonical commercial list. The accepted proposal
      // and companion payment-plan document are consolidated under the signed
      // contract instead of lingering as duplicate cards.
      await cargarDatos();
      alert(data.drive_synced === false
        ? `✅ Documento firmado. ⚠️ Sincronización con Drive pendiente: ${data.drive_warning || 'conexión no disponible.'}`
        : '✅ Documento firmado, cliente y documentos sincronizados con Drive.');
    } catch (error) {
      console.error('Error al firmar:', error);
      alert('❌ Error al firmar el documento');
    }
  };

  // ELIMINAR PROPUESTA
  const handleEliminarPropuesta = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta propuesta?')) return;
    try {
      const proposal = propuestas.find((item) => item.id === id);
      let config = authConfig();
      if (proposal?.estado === 'firmado') {
        const motivo = window.prompt('Indique el motivo para eliminar este contrato firmado. Esta acción retirará sus honorarios del Panel de Control.');
        if (!motivo?.trim()) return;
        config = { ...config, data: { confirmar_firmado: true, motivo: motivo.trim() } };
      }
      
      const { data } = await axios.delete(`${API_URL}/propuestas/${id}`, config);
      const deletedIds = new Set(data?.deleted_ids || [id]);
      setPropuestas((current) => current.filter((item) => !deletedIds.has(item.id)));
      await cargarDatos();
      alert('✅ Propuesta eliminada');
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(`❌ ${error.response?.data?.detail || 'No fue posible eliminar la propuesta.'}`);
    }
  };

  // The API protects legal documents with bearer authentication. Fetch the
  // bytes first; a raw iframe/window.open navigation cannot attach that token.
  const handleDescargar = async (propuesta) => {
    if (!canViewGeneratedPdfs) return;
    try {
      await downloadProtectedDocument(
        `${API_URL}/propuestas/${propuesta.id}/download`,
        `${propuesta.tipo || 'documento'}_${propuesta.id}.pdf`,
      );
    } catch (error) {
      alert(`❌ ${error.message || 'No fue posible descargar el documento.'}`);
    }
  };

  const handleDescargarPaquete = async (propuesta) => {
    if (!propuesta.package_id || !canViewGeneratedPdfs) return;
    try {
      await downloadProtectedDocument(
        `${API_URL}/propuestas/paquetes/${propuesta.package_id}/download-zip`,
        `contrato_y_pagos_${String(propuesta.package_id).slice(0, 8)}.zip`,
      );
    } catch (error) {
      alert(`❌ ${error.message || 'No fue posible descargar el paquete.'}`);
    }
  };

  // ============ ABRIR EDITOR ============
  const handleOpenEditor = (propuesta, packageDocuments = []) => {
    setEditingPropuesta(propuesta);
    setEditingPackageDocuments(packageDocuments);
    setShowEditor(true);
  };

  // ============ COMPONENTE FILA ============
  const TableRow = ({ propuesta }) => {
    const estadoInfo = getEstadoBadge(propuesta.estado);
    const vigenciaInfo = getVigenciaEstado(propuesta);
    
    return (
      <tr className="propuesta-row" key={propuesta.id}>
        <td>
          <div className="propuesta-info">
            <span className="propuesta-titulo">{propuesta.titulo}</span>
            <span className="propuesta-tipo">
              {documentType(propuesta.tipo).label}
            </span>
          </div>
        </td>
        <td>
          <div className="cliente-info">
            <FiUser size={14} />
            <span>{getDestinatarioNombre(propuesta)}</span>
          </div>
        </td>
        <td>
          <span 
            className={`estado-badge ${propuesta.estado}`}
            onClick={() => {
              if (propuesta.tipo === 'contrato' && propuesta.estado === 'enviada') {
                handleFirmarPropuesta(propuesta.id);
              }
            }}
            style={{ cursor: propuesta.tipo === 'contrato' && propuesta.estado === 'enviada' ? 'pointer' : 'default' }}
          >
            {getEstadoIcon(propuesta.estado)} {estadoInfo.label}
          </span>
          {propuesta.estado === 'firmado' && propuesta.fecha_firma && <small className="firma-fecha-hora">Firmado: {formatFechaHora(propuesta.fecha_firma)}</small>}
        </td>
        <td><span className="tipo-readonly">{propuesta.package_id ? '📝 Contrato + Plan de pagos' : documentType(propuesta.tipo).label}</span></td>
        <td>
          {propuesta.fecha_envio ? (
            <div className="fecha-info">
              <FiCalendar size={14} />
              <span>{formatFecha(propuesta.fecha_envio)}</span>
            </div>
          ) : (
            <span className="text-muted">No enviada</span>
          )}
        </td>
        <td>
          {propuesta.fecha_vencimiento && propuesta.estado === 'enviada' && (
            <div className="vigencia-info" style={{ color: vigenciaInfo?.color }}>
              <FiClock size={14} />
              <span>{vigenciaInfo?.label}</span>
            </div>
          )}
          {propuesta.estado !== 'enviada' && (
            <span className="text-muted">-</span>
          )}
        </td>
        <td>
          <div className="actions-group">
            <button 
              className="action-btn view"
              onClick={() => {
                setSelectedPropuesta(propuesta);
                setShowVerModal(true);
              }}
              title="Ver"
            >
              <FiEye />
            </button>
            <button 
              className="action-btn edit"
              onClick={() => handleOpenEditor(propuesta)}
              title="Editar"
            >
              <FiEdit2 />
            </button>
            {propuesta.estado !== 'firmado' && propuesta.estado !== 'archivado' && (
              <>
                <button 
                  className="action-btn edit"
                  onClick={() => {
                    setSelectedPropuesta(propuesta);
                    setShowEnviarModal(true);
                    setEnvioData({
                      email_cliente: getDestinatarioEmail(propuesta),
                      mensaje_adicional: ''
                    });
                  }}
                  title="Enviar"
                >
                  <FiSend />
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleEliminarPropuesta(propuesta.id)}
                  title="Eliminar"
                >
                  <FiTrash2 />
                </button>
              </>
            )}
            {propuesta.estado === 'firmado' && (
              <button className="action-btn delete" onClick={() => handleEliminarPropuesta(propuesta.id)} title="Eliminar contrato firmado con motivo">
                <FiTrash2 />
              </button>
            )}
            {propuesta.tipo === 'contrato' && propuesta.estado === 'enviada' && (
              <button 
                className="action-btn firmar"
                onClick={() => handleFirmarPropuesta(propuesta.id)}
                title="Confirmar firma recibida"
              >
                <FiCheckCircle />
              </button>
            )}
            {canViewGeneratedPdfs && <button
              className="action-btn download"
              onClick={() => handleDescargar(propuesta)}
              title="Descargar PDF"
            >
              <FiDownload />
            </button>}
            {canViewGeneratedPdfs && propuesta.package_id && <button className="action-btn download" onClick={() => handleDescargarPaquete(propuesta)} title="Descargar paquete ZIP">ZIP</button>}
          </div>
        </td>
      </tr>
    );
  };

  // ============ RENDERIZADO PRINCIPAL ============
  return (
    <div className="propuestas-module">
      {/* Header */}
      <div className="propuestas-header">
        <div className="header-left">
          <h1>📄 Propuestas y Contratos</h1>
          <p>Gestión de documentos legales</p>
        </div>
        <button className="btn-crear" onClick={abrirCrearDocumento}>
          <FiPlus /> Crear Nuevo
        </button>
      </div>

      {/* Stats Cards - Clickeables */}
      <div className="propuestas-stats">
        <div 
          className="stat-card propuestas clickable"
          onClick={() => setFilterTipo(filterTipo === 'propuesta' ? 'todos' : 'propuesta')}
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-icon">📄</span>
          <div className="stat-info">
            <span className="stat-number">{propuestas.filter(p => p.tipo === 'propuesta').length}</span>
            <span className="stat-label">Propuestas</span>
          </div>
        </div>
        <div 
          className="stat-card contratos clickable"
          onClick={() => setFilterTipo(filterTipo === 'contrato' ? 'todos' : 'contrato')}
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-icon">📝</span>
          <div className="stat-info">
            <span className="stat-number">{propuestas.filter(p => p.tipo === 'contrato').length}</span>
            <span className="stat-label">Contratos</span>
          </div>
        </div>
        <div 
          className="stat-card enviadas clickable"
          onClick={() => setFilterEstado(filterEstado === 'enviada' ? 'todos' : 'enviada')}
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-icon">📤</span>
          <div className="stat-info">
            <span className="stat-number">{propuestas.filter(p => p.estado === 'enviada').length}</span>
            <span className="stat-label">Enviados</span>
          </div>
        </div>
        <div 
          className="stat-card firmadas clickable"
          onClick={() => setFilterEstado(filterEstado === 'firmado' ? 'todos' : 'firmado')}
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-icon">✅</span>
          <div className="stat-info">
            <span className="stat-number">{propuestas.filter(p => p.estado === 'firmado').length}</span>
            <span className="stat-label">Firmados</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="propuestas-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por título o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
          >
            <option value="todos">Todos los tipos</option>
            <option value="propuesta">📄 Propuestas</option>
            <option value="contrato">📝 Contratos</option>
            <option value="plan_pagos">💳 Planes de pagos</option>
          </select>
          <select
            className="filter-select"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
          >
            <option value="todos">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="enviada">Enviada</option>
            <option value="firmado">Firmado</option>
            <option value="vencido">Vencido</option>
            <option value="rechazado">Rechazado</option>
          </select>
          <button className="btn-refresh" onClick={cargarDatos}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="propuestas-table-container">
        <table className="propuestas-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Lead / Cliente firmado</th>
              <th>Estado</th>
              <th>Tipo</th>
              <th>Fecha Envío</th>
              <th>Vigencia</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell"><div className="loader"></div><p>Cargando...</p></td></tr>
            ) : currentPropuestas.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">
                <div className="empty-state">
                  <FiFileText className="empty-icon" />
                  <h3>No hay documentos</h3>
                  <p>Crear una nueva propuesta o contrato</p>
                </div>
              </td></tr>
            ) : (
              currentPropuestas.map(propuesta => <TableRow key={propuesta.id} propuesta={propuesta} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {filteredPropuestas.length > itemsPerPage && (
        <div className="pagination">
          <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            Anterior
          </button>
          <span className="page-info">Página {currentPage} de {totalPages}</span>
          <button className="page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Siguiente
          </button>
        </div>
      )}

      {/* ============ MODAL CREAR ============ */}
      {showCrearModal && (
        <div className="modal-overlay" onClick={() => setShowCrearModal(false)}>
          <div className="modal-propuesta" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📄 Nueva Propuesta/Contrato</h2>
              <button className="modal-close" onClick={() => setShowCrearModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCrearPropuesta} className="propuesta-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo *</label>
                    <select
                      value={nuevaPropuesta.tipo}
                      onChange={(e) => cargarPlantillaOficial(e.target.value)}
                      required
                    >
                      <option value="propuesta">📄 Propuesta</option>
                      <option value="contrato">📝 Contrato</option>
                      <option value="representacion_accidente" disabled>🧾 Representación Accidente — plantilla pendiente</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Título *</label>
                    <input
                      type="text"
                      value={nuevaPropuesta.titulo}
                      onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, titulo: e.target.value})}
                      placeholder="Título del documento"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Destinatario *</label><select value={nuevaPropuesta.destinatario_tipo} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, destinatario_tipo:e.target.value, lead_id:null, cliente_id:null, nombre_destinatario:''})}><option value="lead">Lead</option><option value="cliente">Cliente</option></select></div>
                  <div className="form-group"><label>{nuevaPropuesta.destinatario_tipo === 'lead' ? 'Lead' : 'Cliente'} destinatario *</label><select value={nuevaPropuesta.destinatario_tipo === 'lead' ? nuevaPropuesta.lead_id || '' : nuevaPropuesta.cliente_id || ''} onChange={(e) => { const id=e.target.value; const source=nuevaPropuesta.destinatario_tipo === 'lead' ? leads : clientes; const selected=source.find(item => String(item.id) === String(id)); setNuevaPropuesta({...nuevaPropuesta, lead_id:nuevaPropuesta.destinatario_tipo === 'lead' ? id : null, cliente_id:nuevaPropuesta.destinatario_tipo === 'cliente' ? id : null, nombre_destinatario:selected ? `${selected.nombre} ${selected.apellido}` : ''}); }} required><option value="">Seleccionar...</option>{(nuevaPropuesta.destinatario_tipo === 'lead' ? leads : clientes).map(item => <option key={item.id} value={item.id}>{item.nombre} {item.apellido} · {item.email}</option>)}</select></div>
                </div>
                <div className="form-group">
                  <label>Abogado Responsable</label>
                  <input
                    type="text"
                    value={nuevaPropuesta.abogado_responsable}
                    onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, abogado_responsable: e.target.value})}
                    placeholder="Nombre del abogado"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre que aparecerá en el documento</label>
                  <input value={nuevaPropuesta.nombre_destinatario} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, nombre_destinatario: e.target.value})} placeholder="Nombre completo del Lead o Cliente" />
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Tipos de proceso</label><div className="process-checkbox-list" role="group" aria-label="Tipos de proceso">{CASE_TYPES.map((caseType) => <label key={caseType.value} className="process-checkbox"><input type="checkbox" checked={nuevaPropuesta.tipos_caso.includes(caseType.value)} onChange={() => toggleCaseType(caseType.value)} /><span>{caseType.label}</span></label>)}</div><small>Marque uno o varios procesos. La selección funciona igual en escritorio y móvil.</small></div>
                  <div className="form-group"><label>Honorarios</label><input type="number" min="0" value={nuevaPropuesta.honorarios} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, honorarios:e.target.value})} /></div>
                  <div className="form-group"><label>Idioma</label><select value={nuevaPropuesta.idioma} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, idioma:e.target.value})}><option value="es">Español</option><option value="en">English</option></select></div>
                </div>
                <div className="form-group"><label>Otro tipo de proceso</label><input value={nuevaPropuesta.tipo_caso_personalizado} onChange={(event) => setNuevaPropuesta({...nuevaPropuesta, tipo_caso_personalizado:event.target.value})} placeholder="Escriba uno o varios procesos no listados; sepárelos con coma" /><small>Se incorpora literalmente en la propuesta, contrato, vista previa y PDF. Para PDF en inglés, escriba este texto en inglés.</small></div>
                {nuevaPropuesta.tipo === 'propuesta' && <section className="new-proposal-payment-options"><div className="plan-heading">💳 Opciones de pago que verá el cliente</div><div className="form-group"><label>Cantidad de opciones (1 a 10)</label><input type="number" min="1" max="10" value={nuevaPropuesta.opciones_pago} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, opciones_pago:clampPaymentOptionCount(e.target.value)})} /><small>Predeterminado: 2. Incluye todas las alternativas desde 1 pago hasta la cantidad indicada.</small></div><div className="new-payment-preview">{calculatePaymentOptions(nuevaPropuesta.honorarios, nuevaPropuesta.opciones_pago).map(option => <div key={option.payments}><strong>{option.payments} pago{option.payments === 1 ? '' : 's'}</strong><span>{option.discount ? `${option.discount}% descuento` : `${option.surcharge}% cargo`}</span><b>{formatPaymentMoney(option.total)}</b><small>{formatPaymentMoney(option.installment)} por pago</small></div>)}</div></section>}
                {nuevaPropuesta.tipo === 'contrato' && <div className="form-row"><div className="form-group"><label>Tipo de fee gubernamental</label><select value={nuevaPropuesta.tipo_tarifa_gubernamental} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, tipo_tarifa_gubernamental:e.target.value})}><option>Fee USCIS</option><option>Fee Corte Federal</option><option>Fee Corte Civil/Familia</option><option>Fee BIA</option></select></div><div className="form-group"><label>Valor del fee gubernamental</label><input type="number" min="0" step="0.01" value={nuevaPropuesta.tarifa_gubernamental} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, tarifa_gubernamental:e.target.value})} placeholder="0.00" /></div></div>}
                {nuevaPropuesta.tipo === 'contrato' && <div className="plan-fields"><div className="plan-heading">💳 Plan de pagos incluido con el contrato</div><div className="form-row"><div className="form-group"><label>Depósito inicial</label><input type="number" min="0" max={nuevaPropuesta.honorarios} value={nuevaPropuesta.deposito_inicial} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, deposito_inicial:e.target.value})}/></div><div className="form-group"><label>Saldo restante</label><input type="text" readOnly value={`$${Math.max(0, Number(nuevaPropuesta.honorarios || 0) - Number(nuevaPropuesta.deposito_inicial || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} /></div><div className="form-group"><label>Cuotas (máximo 10)</label><input type="number" min="1" max="10" required value={nuevaPropuesta.numero_cuotas} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, numero_cuotas:e.target.value})}/></div><div className="form-group"><label>Frecuencia</label><select value={nuevaPropuesta.frecuencia} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, frecuencia:e.target.value})}><option value="mensual">Mensual</option><option value="quincenal">Quincenal</option></select></div></div><div className="form-row"><div className="form-group"><label>Primer pago *</label><input type="date" required value={nuevaPropuesta.fecha_primer_pago} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, fecha_primer_pago:e.target.value})}/></div><div className="form-group"><label>Método preferido</label><select value={nuevaPropuesta.metodo_pago} onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, metodo_pago:e.target.value})}><option value="card_one">Tarjeta / enlace de pago</option><option value="zelle">Zelle</option><option value="manual">Otro</option></select></div></div></div>}
                <div className="form-group document-template-status">
                  <label>Documento oficial</label>
                  <div className="template-ready-card"><span>📄</span><div><b>Plantilla oficial lista</b><small>Al crear, el CRM generará el PDF con el Lead, tipo de caso, honorarios e idioma seleccionados. La acción “Ver” abrirá el PDF, no el HTML.</small></div></div>
                  <textarea value={nuevaPropuesta.contenido} onChange={(e) => handleContenidoChange(e.target.value)} aria-label="Plantilla oficial" required className="contenido-editor-hidden" />
                </div>
                <div className="form-group">
                  <label>Notas Internas</label>
                  <textarea
                    value={nuevaPropuesta.notas}
                    onChange={(e) => setNuevaPropuesta({...nuevaPropuesta, notas: e.target.value})}
                    placeholder="Notas internas..."
                    rows="3"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowCrearModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-save">
                    Crear documento
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL VER ============ */}
      {showVerModal && selectedPropuesta && (
        <div className="modal-overlay" onClick={() => setShowVerModal(false)}>
          <div className="modal-propuesta large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPropuesta.titulo}</h2>
              <button className="modal-close" onClick={() => setShowVerModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="propuesta-detalle">
                <div className="detalle-info">
                  <div className="info-item">
                    <span className="label">{getDestinatarioTipo(selectedPropuesta)}</span>
                    <span className="value">{getDestinatarioNombre(selectedPropuesta)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Tipo</span>
                    <span className="value">{documentType(selectedPropuesta.tipo).label}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Estado</span>
                    <span className="value">
                      <span 
                        className={`estado-badge ${selectedPropuesta.estado}`}
                      >
                        {getEstadoIcon(selectedPropuesta.estado)} {getEstadoBadge(selectedPropuesta.estado).label}
                      </span>
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Fecha Envío</span>
                    <span className="value">{formatFecha(selectedPropuesta.fecha_envio)}</span>
                  </div>
                  {selectedPropuesta.fecha_vencimiento && (
                    <div className="info-item">
                      <span className="label">Vigencia</span>
                      <span className="value">
                        {formatFecha(selectedPropuesta.fecha_vencimiento)}
                        {getVigenciaEstado(selectedPropuesta) && (
                          <span style={{ color: getVigenciaEstado(selectedPropuesta)?.color, marginLeft: '8px' }}>
                            ({getVigenciaEstado(selectedPropuesta)?.label})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {selectedPropuesta.abogado_responsable && (
                    <div className="info-item">
                      <span className="label">Abogado Responsable</span>
                      <span className="value">{selectedPropuesta.abogado_responsable}</span>
                    </div>
                  )}
                </div>
                <div className="detalle-contenido pdf-document-preview">
                  <h4>Vista previa del PDF oficial</h4>
                  {!canViewGeneratedPdfs && <div className="pdf-preview-status is-restricted">Su rol no tiene acceso a propuestas, contratos ni PDF generados.</div>}
                  {canViewGeneratedPdfs && proposalPreview.loading && <div className="pdf-preview-status">Cargando documento protegido…</div>}
                  {canViewGeneratedPdfs && proposalPreview.error && <div className="pdf-preview-status is-error">{proposalPreview.error}</div>}
                  {canViewGeneratedPdfs && proposalPreview.url && <iframe title={`PDF ${selectedPropuesta.titulo}`} src={proposalPreview.url} />}
                </div>
                {selectedPropuesta.notas && (
                  <div className="detalle-notas">
                    <h4>Notas</h4>
                    <p>{selectedPropuesta.notas}</p>
                  </div>
                )}
                <div className="detalle-acciones">
                  {selectedPropuesta.estado === 'borrador' && (
                    <>
                      <button 
                        className="btn-enviar"
                        onClick={() => {
                          setShowEnviarModal(true);
                          setEnvioData({
                            email_cliente: getDestinatarioEmail(selectedPropuesta),
                            mensaje_adicional: ''
                          });
                        }}
                      >
                        <FiSend /> Enviar
                      </button>
                      <button 
                        className="btn-edit"
                        onClick={() => handleOpenEditor(selectedPropuesta)}
                      >
                        <FiEdit2 /> Editar
                      </button>
                    </>
                  )}
                  {selectedPropuesta.tipo === 'contrato' && selectedPropuesta.estado === 'enviada' && (
                    <button 
                      className="btn-firmar"
                      onClick={() => handleFirmarPropuesta(selectedPropuesta.id)}
                    >
                      <FiCheckCircle /> Confirmar firma recibida
                    </button>
                  )}
                  {canViewGeneratedPdfs && <button
                    className="btn-download"
                    onClick={() => handleDescargar(selectedPropuesta)}
                  >
                    <FiDownload /> Descargar PDF
                  </button>}
                  {canViewGeneratedPdfs && selectedPropuesta.package_id && <button className="btn-download" onClick={() => handleDescargarPaquete(selectedPropuesta)}><FiDownload /> Descargar paquete ZIP</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL ENVIAR ============ */}
      {showEnviarModal && selectedPropuesta && (
        <div className="modal-overlay" onClick={() => setShowEnviarModal(false)}>
          <div className="modal-propuesta" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📤 Enviar {selectedPropuesta.tipo}</h2>
              <button className="modal-close" onClick={() => setShowEnviarModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="envio-info">
                <p><strong>Documento:</strong> {selectedPropuesta.titulo}</p>
                <p><strong>{getDestinatarioTipo(selectedPropuesta)}:</strong> {getDestinatarioNombre(selectedPropuesta)}</p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleEnviarPropuesta(); }}>
                <div className="form-group">
                  <label>Email del {getDestinatarioTipo(selectedPropuesta)} *</label>
                  <input
                    type="email"
                    value={envioData.email_cliente}
                    onChange={(e) => setEnvioData({...envioData, email_cliente: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mensaje Adicional</label>
                  <textarea
                    value={envioData.mensaje_adicional}
                    onChange={(e) => setEnvioData({...envioData, mensaje_adicional: e.target.value})}
                    placeholder="Mensaje personalizado para el cliente..."
                    rows="4"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEnviarModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-save" disabled={loadingEnvio}>
                    {loadingEnvio ? 'Enviando...' : <><FiSend /> Enviar</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ============ EDITOR DE PROPUESTA ============ */}
      {showEditor && editingPropuesta && (
        <PropuestaEditor
          propuesta={editingPropuesta}
          packageDocuments={editingPackageDocuments}
          onClose={() => {
            setShowEditor(false);
            setEditingPropuesta(null);
            setEditingPackageDocuments([]);
          }}
          onSave={(updatedDocuments) => {
            const documents = Array.isArray(updatedDocuments) ? updatedDocuments : [updatedDocuments];
            setPropuestas(current => current.map(item => documents.find(updated => updated.id === item.id) || item));
            setEditingPackageDocuments(documents);
            const active = documents.find(item => item.id === editingPropuesta.id) || documents[0];
            if (active) setEditingPropuesta(active);
          }}
        />
      )}
    </div>
  );
};

export default Propuestas;
